import {useEffect, useMemo} from 'react'
import {useAusbildungsplan} from '#hooks/useAusbildungsplan'
import {useCurrentUserId} from '#hooks/store/useCurrentUserId'
import {useAzubiList} from '#hooks/store/useAzubiList'
import {useI18n} from '#hooks/useI18n'
import {routeKeys, routeHelper} from '#config/routeKeys'
import {usePermissions} from './usePermissions'
import actions from 'redux-auto'
import {useParams} from 'react-router-dom'

const splitIntoBreadcrumbParts = pathname =>
  pathname.split('/').filter(x => !!x && !['static', 'questions'].includes(x))
const addBreadcrumbFunc = breadcrumbs => (url, title) => {
  breadcrumbs.push({
    menuItemId: url,
    title,
  })
}
const getContextualUserId = (userContext, defaultValue) => {
  if (userContext) {
    return parseInt(userContext, 10)
  }
  return defaultValue
}

const getUserId = (permissions, currentUserId, azubiIdInUrl) => {
  if (permissions.ausbildungsplan.viewAusbildungsplanFromOthers && currentUserId !== azubiIdInUrl) {
    return azubiIdInUrl
  }
  if (permissions.ausbildungsplan.edit) {
    return currentUserId
  }
  return null
}

export const useBreadcrumbs = pathname => {
  const {currentUserId} = useCurrentUserId()
  const permissions = usePermissions()
  const params = useParams()
  const azubiIdInUrl = params.azubiId ? parseInt(params.azubiId, 10) : null

  const userId = getUserId(permissions, currentUserId, azubiIdInUrl)
  const {ready, findParent} = useAusbildungsplan(userId)
  const {allAzubis} = useAzubiList()
  useEffect(() => {
    actions.filter.setAzubisInFilter({azubis: allAzubis})
  }, [allAzubis])

  const i18n = useI18n()

  return useMemo(() => {
    const newBreadcrumbs = []
    const addBreadcrumb = addBreadcrumbFunc(newBreadcrumbs)

    addBreadcrumb('/', i18n.t('breadcrumb_root'))
    const [firstPart, userContext, chapter] = splitIntoBreadcrumbParts(pathname)
    if (firstPart === 'ausbildungsplan') {
      addBreadcrumb(routeKeys.ausbildungsplan, i18n.t('breadcrumb_ausbildungsplan'))

      const contextualUserId = getContextualUserId(userContext, currentUserId)
      const isStaticContext = pathname.includes('static')

      if (userContext && currentUserId !== contextualUserId) {
        const contextualUser = (allAzubis && allAzubis.find(x => x.id === contextualUserId)) || {
          name: userContext,
        }
        const url = routeHelper.ausbildungsplanForUser(contextualUserId)
        addBreadcrumb(url, contextualUser.name)
      }

      if (chapter && ready && !isStaticContext) {
        const ausbildungsplankapitel = findParent(chapter) || {text: chapter}
        const url = routeHelper.ausbildungsplanQuestionsChapter(contextualUserId, chapter)
        addBreadcrumb(url, ausbildungsplankapitel.text)
      }
    }
    return newBreadcrumbs
  }, [pathname, ready, allAzubis.length])
}
